import { useEffect, useRef } from 'react';

interface WalletNode {
  x: number;
  y: number;
  vx: number;
  vy: number;
  label: string;
  r: number;
  glowPhase: number;
  activeLineTimer: number;
  activeLineCooldown: number;
  activeLineTarget: number;
}

const ADDRESSES = [
  '7HxP...9e2d', 'Dm4Q...K9Lm', 'K9Lm...T8Xa', 'T8Xa...3RfN', '3RfN...Bw2Y',
  'Bw2Y...P7Vc', 'P7Vc...Jq5W', 'Jq5W...E1St', 'E1St...Gz8K', 'Gz8K...4LuM',
  '4LuM...Hn3P', 'Hn3P...Xy9Q', 'Xy9Q...Ac6R', 'Ac6R...Vs2T', 'Vs2T...Fj4W',
  'Fj4W...Nk7B', 'Nk7B...Yd1C', 'Yd1C...Zt5E', 'Zt5E...Gh9J', 'Gh9J...M3Ka',
  'M3Ka...Q8Lr', 'Q8Lr...U4Ns', 'U4Ns...W6Dv', 'W6Dv...Ie2O', 'Ie2O...P5Hu',
  'P5Hu...R1Fx', 'R1Fx...S9Gy', 'S9Gy...T4Jw', 'T4Jw...V7Kz', 'V7Kz...2Ab',
  '8JdP...Xa92', 'Gj8k...F2Qa', 'Ax4P...Mn7Q', 'Ep7B...Gd4S', 'Cn8T...Jk3W',
];

/**
 * Decorative slow-drifting wallet network background.
 * Renders wallet-address nodes connected by faint lines at very low opacity.
 * Enhanced with glowing node appearances, active connection pulses, and soft fades.
 */
export default function WalletNetworkBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let width = 0;
    let height = 0;
    let nodes: WalletNode[] = [];
    let raf = 0;

    const init = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = canvas.clientWidth;
      height = canvas.clientHeight;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.scale(dpr, dpr);

      const count = Math.max(12, Math.min(24, Math.round((width * height) / 50000)));
      nodes = Array.from({ length: count }, (_, i) => ({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.08,
        vy: (Math.random() - 0.5) * 0.08,
        label: ADDRESSES[i % ADDRESSES.length],
        r: Math.random() * 2 + 2,
        glowPhase: Math.random() * Math.PI * 2,
        activeLineTimer: 0,
        activeLineCooldown: Math.random() * 200 + 100,
        activeLineTarget: -1,
      }));
    };

    let time = 0;

    const step = () => {
      time += 1;
      ctx.clearRect(0, 0, width, height);

      // Update nodes
      for (const n of nodes) {
        n.x += n.vx;
        n.y += n.vy;
        if (n.x < 40) n.vx = Math.abs(n.vx);
        if (n.x > width - 40) n.vx = -Math.abs(n.vx);
        if (n.y < 20) n.vy = Math.abs(n.vy);
        if (n.y > height - 20) n.vy = -Math.abs(n.vy);

        n.activeLineCooldown -= 1;
        if (n.activeLineTimer > 0) {
          n.activeLineTimer -= 1;
        } else if (n.activeLineCooldown <= 0) {
          if (Math.random() < 0.005) {
            // Pick a random nearby target
            const candidates = nodes
              .map((other, idx) => ({ other, idx, dist: Math.hypot(n.x - other.x, n.y - other.y) }))
              .filter((c) => c.idx !== nodes.indexOf(n) && c.dist < 250)
              .sort((a, b) => a.dist - b.dist)
              .slice(0, 5);
            if (candidates.length > 0) {
              const pick = candidates[Math.floor(Math.random() * candidates.length)];
              n.activeLineTarget = pick.idx;
              n.activeLineTimer = 90 + Math.random() * 60;
              n.activeLineCooldown = 250 + Math.random() * 400;
            } else {
              n.activeLineCooldown = 60;
            }
          }
        }
      }

      const linkDist = 180;
      const lineBaseOpacity = 0.08;

      // Draw connections
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const a = nodes[i];
          const b = nodes[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < linkDist) {
            const linePulse = (Math.sin(time * 0.001 + i * 0.3) + 1) / 2;
            const opacity = (1 - dist / linkDist) * lineBaseOpacity * (0.7 + linePulse * 0.3);
            ctx.strokeStyle = `rgba(20, 224, 176, ${opacity})`;
            ctx.lineWidth = 0.5;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }

      // Draw active connection pulses
      for (let i = 0; i < nodes.length; i++) {
        const n = nodes[i];
        if (n.activeLineTimer > 0 && n.activeLineTarget >= 0) {
          const target = nodes[n.activeLineTarget];
          if (target) {
            const progress = 1 - n.activeLineTimer / 150;
            const pulsePos = progress;
            const px = n.x + (target.x - n.x) * pulsePos;
            const py = n.y + (target.y - n.y) * pulsePos;
            const intensity = Math.sin(progress * Math.PI);

            ctx.beginPath();
            ctx.arc(px, py, 3 + intensity * 3, 0, Math.PI * 2);
            ctx.fillStyle = `rgba(20, 224, 176, ${0.15 * intensity})`;
            ctx.fill();

            ctx.beginPath();
            ctx.arc(px, py, 1.2, 0, Math.PI * 2);
            ctx.fillStyle = `rgba(255, 255, 255, ${0.6 * intensity})`;
            ctx.fill();
          }
        }
      }

      // Draw nodes
      for (let i = 0; i < nodes.length; i++) {
        const n = nodes[i];
        const glowPulse = (Math.sin(time * 0.002 + n.glowPhase) + 1) / 2;

        // Slow pulse glow ring
        const glowR = n.r + 2 + glowPulse * 5;
        ctx.beginPath();
        ctx.arc(n.x, n.y, glowR, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(20, 224, 176, ${0.02 + glowPulse * 0.04})`;
        ctx.fill();

        // Core node
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(20, 224, 176, 0.12)';
        ctx.fill();

        // Active glow when line is pulsing
        if (n.activeLineTimer > 0) {
          const activeIntensity = Math.sin((1 - n.activeLineTimer / 150) * Math.PI);
          ctx.beginPath();
          ctx.arc(n.x, n.y, n.r + 4, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(20, 224, 176, ${0.06 * activeIntensity})`;
          ctx.fill();
        }

        ctx.font = '500 9px "Hanken Grotesk", monospace';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = 'rgba(161, 161, 170, 0.10)';
        ctx.fillText(n.label, n.x, n.y + n.r + 10);
      }

      raf = requestAnimationFrame(step);
    };

    init();
    step();

    const onResize = () => {
      cancelAnimationFrame(raf);
      init();
      step();
    };
    window.addEventListener('resize', onResize);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="pointer-events-none fixed inset-0 z-[1] h-full w-full"
    />
  );
}
